/**
 * Test Count Tracker Service
 * Free tier: max 3 practice tests. Tracks FREE_AI_EXAM_PENDING, FREE_AI_EXAM_USED.
 * Subscribed: tracks unique topics for AI exam recommendation (5, 10, 15, 20...).
 */

import { isUserSubscribed } from './subscriptionGate';
import { STORAGE_KEYS } from '../constants/storageKeys';

const STORAGE_KEY = 'practiceTestCount';
const MAX_FREE_PRACTICE_TESTS = 3;
const AI_EXAM_TOPIC_THRESHOLD = 5; // 5, 10, 15, 20...

interface TestCountData {
  count: number;
  lastUpdated: string;
}

class TestCountTrackerService {
  private cachedCount: number | null = null;
  private cachedCountTimestamp = 0;
  private readonly CACHE_TTL = 1000;

  /** Raw stored count (uncapped). Subscribed users need full total for AI exam every-10 unlocks. */
  private getRawCount(): number {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return 0;
      const data: TestCountData = JSON.parse(stored);
      const n = data?.count ?? 0;
      return Number.isFinite(n) && n >= 0 ? n : 0;
    } catch {
      return 0;
    }
  }

  incrementTestCount(): void {
    const raw = this.getRawCount();
    if (!isUserSubscribed()) {
      if (raw >= MAX_FREE_PRACTICE_TESTS) return;
    }
    const newCount = raw + 1;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      count: newCount,
      lastUpdated: new Date().toISOString()
    }));
    this.cachedCount = newCount;
    this.cachedCountTimestamp = Date.now();
  }

  /**
   * Total practice tests completed (stored). Free tier: capped at 3 for gate/UI; subscribed: uncapped.
   */
  getTestCount(): number {
    const now = Date.now();
    if (this.cachedCount !== null && (now - this.cachedCountTimestamp) < this.CACHE_TTL) {
      if (!isUserSubscribed()) {
        return Math.min(this.cachedCount, MAX_FREE_PRACTICE_TESTS);
      }
      return this.cachedCount;
    }
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        this.cachedCount = 0;
        this.cachedCountTimestamp = now;
        return 0;
      }
      const data: TestCountData = JSON.parse(stored);
      const raw = data?.count ?? 0;
      const count = Number.isFinite(raw) && raw >= 0 ? raw : 0;
      this.cachedCount = count;
      this.cachedCountTimestamp = now;
      if (!isUserSubscribed()) {
        return Math.min(count, MAX_FREE_PRACTICE_TESTS);
      }
      return count;
    } catch {
      this.cachedCount = 0;
      this.cachedCountTimestamp = now;
      return 0;
    }
  }

  resetTestCount(): void {
    localStorage.removeItem(STORAGE_KEY);
    this.cachedCount = null;
    this.cachedCountTimestamp = 0;
  }

  invalidateCache(): void {
    this.cachedCount = null;
    this.cachedCountTimestamp = 0;
  }

  /** Free user: after 3rd test, set FREE_AI_EXAM_PENDING based on weak areas */
  setFreeAiExamPending(hasWeakArea: boolean): void {
    localStorage.setItem(STORAGE_KEYS.FREE_AI_EXAM_PENDING, hasWeakArea ? 'true' : 'false');
  }

  getFreeAiExamPending(): boolean {
    return localStorage.getItem(STORAGE_KEYS.FREE_AI_EXAM_PENDING) === 'true';
  }

  /** Free AI exam: one per user. After use, set to true */
  setFreeAiExamUsed(): void {
    localStorage.setItem(STORAGE_KEYS.AI_EXAM_FREE_USED, 'true');
  }

  getFreeAiExamUsed(): boolean {
    return localStorage.getItem(STORAGE_KEYS.AI_EXAM_FREE_USED) === 'true';
  }

  /** Subscribed: add unique topic when user completes a practice test for that topic (first completion only) */
  addUniqueTopicCompleted(topicId: string): void {
    const raw = localStorage.getItem(STORAGE_KEYS.UNIQUE_TOPICS_COMPLETED);
    let topics: string[];
    try {
      topics = raw ? JSON.parse(raw) : [];
    } catch {
      topics = [];
    }
    if (!Array.isArray(topics)) topics = [];
    if (topics.includes(topicId)) return;
    topics.push(topicId);
    localStorage.setItem(STORAGE_KEYS.UNIQUE_TOPICS_COMPLETED, JSON.stringify(topics));
  }

  getUniqueTopicsCompleted(): number {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.UNIQUE_TOPICS_COMPLETED);
      const topics = raw ? JSON.parse(raw) : [];
      return Array.isArray(topics) ? topics.length : 0;
    } catch {
      return 0;
    }
  }

  /** Next threshold for AI exam: 5, 10, 15, 20... (based on exams already taken) */
  getNextAiExamThreshold(examsTaken: number): number {
    return AI_EXAM_TOPIC_THRESHOLD + examsTaken * AI_EXAM_TOPIC_THRESHOLD;
  }

  /** Subscribed: AI exam recommended when uniqueTopics >= threshold AND weak area exists */
  shouldRecommendAiExam(uniqueTopics: number, hasWeakArea: boolean, examsTaken: number): boolean {
    const threshold = this.getNextAiExamThreshold(examsTaken);
    return uniqueTopics >= threshold && hasWeakArea;
  }

  hasPassedSubscriptionGate(): boolean {
    return isUserSubscribed();
  }

  /** @deprecated Soft gate removed - always false */
  shouldShowSoftGate(): boolean {
    return false;
  }

  /** @deprecated Replaced by HardGate - always false */
  shouldShowSubscriptionGate(): boolean {
    return false;
  }

  markSubscriptionGatePassed(): void {
    // No-op: subscription status comes from subscriptionService/localStorage
    this.invalidateCache();
  }
}

export const testCountTracker = new TestCountTrackerService();
